import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Redirect } from "wouter";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";

// The server's live master + user grant check is authoritative in all runtimes.
export function SmbAdminGuard({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { data, isPending, isFetching, isFetchedAfterMount, isError } = useQuery<{ enabled: boolean }>({
    // Separate from the sidebar observer: its mount refetch must not unmount
    // its own layout by changing the screen guard's verification state.
    queryKey: ["/api/admin/smb/access", user?.id, "screen"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/smb/access");
      return z.object({ enabled: z.boolean() }).parse(await response.json());
    },
    enabled: !!user?.isMaster && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  if (authLoading || (user?.isMaster && (isPending || isFetching || !isFetchedAfterMount))) return null;
  if (!user?.isMaster || isError || data?.enabled !== true) return <Redirect to="/" />;
  return <>{children}</>;
}
